/**
 * Landing Page Component
 * Public-facing page for unauthenticated visitors to learn about the portal
 */

import React from 'react';
import { Link } from 'react-router-dom';
import './LandingPage.css';

/**
 * Renders the appropriate icon based on type
 * - Number (1-33): Custom SVG icon from /images/Rapid Fusion Website Icons/
 * - String: Font Awesome icon class
 */
const FeatureIcon = ({ icon }) => {
  if (typeof icon === 'number') {
    return (
      <img 
        src={`/images/Rapid Fusion Website Icons/${icon}.svg`} 
        alt="" 
        className="rf-landing-svg-icon"
      />
    );
  }
  return <i className={`fas fa-${icon}`}></i>;
};

const LandingPage = () => {
  const features = [
    {
      icon: 30, // Checklist/documentation icon
      title: 'User Guides & Documentation',
      description: 'Manuals for Apollo, Zeus, Medusa and Cerberus cells — PE320, Epicurus Controller Pro, Air Plus, heated beds, and the robot.',
    },
    {
      icon: 12, // Ruler/measurement icon
      title: 'Calibration Tools',
      description: 'Precision calibration utilities to keep your equipment running at peak performance.',
    },
    {
      icon: 6, // Person presenting icon
      title: 'Training & Certification',
      description: 'Complete operator and calibration training programs with certification upon completion.',
    },
    {
      icon: 27, // Complex integration/atomic structure icon
      title: 'Cloud Service Platform',
      description: 'Centralized cloud-based control center for managing and monitoring connected manufacturing equipment.',
    },
    {
      icon: 20, // Bar chart with trend icon
      title: 'Sales & Analytics Tools',
      description: 'ROI calculators, commission trackers, pilot project tools, and reseller dashboards to drive your business forward.',
    },
    {
      icon: 1, // Support/partnership hand icon
      title: 'Reseller Resources',
      description: 'Access marketing materials, product sheets, pricing tools, and sales resources designed for Rapid Fusion resellers.',
    },
  ];

  return (
    <div className="rf-landing">
      {/* Hero Section */}
      <section className="rf-landing-hero">
        <div className="rf-landing-hero-content">
          <h1 className="rf-landing-hero-title">
            Welcome to the Rapid Fusion Resource Portal
          </h1>
          <p className="rf-landing-hero-subtitle">
            Your centralized hub for tools, training, and resources powering the future of large-format 3D printing with pellet extrusion technology.
          </p>
          <div className="rf-landing-hero-actions">
            <Link to="/login" className="rf-btn rf-btn-primary rf-btn-lg">
              <i className="fas fa-sign-in-alt"></i>
              Log In
            </Link>
            <Link to="/request-access" className="rf-btn rf-btn-outline-light rf-btn-lg">
              <i className="fas fa-user-plus"></i>
              Request Access
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="rf-landing-features">
        <div className="rf-container">
          <div className="rf-landing-section-header">
            <h2>Everything You Need in One Place</h2>
            <p>The Rapid Fusion Resource Portal gives authorized resellers and operators access to a full suite of tools and resources.</p>
          </div>
          <div className="rf-landing-features-grid">
            {features.map((feature, index) => (
              <div key={index} className="rf-landing-feature-card">
                <div className="rf-landing-feature-icon">
                  <FeatureIcon icon={feature.icon} />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="rf-landing-how-it-works">
        <div className="rf-container">
          <div className="rf-landing-section-header">
            <h2>Get Started in Three Steps</h2>
          </div>
          <div className="rf-landing-steps">
            <div className="rf-landing-step">
              <div className="rf-landing-step-number">
                <img src="/images/Rapid Fusion Website Icons/21.svg" alt="Step 1" className="rf-landing-step-icon" />
              </div>
              <h3>Request Access</h3>
              <p>Submit a request with your details. Eligible resellers and operators are approved quickly.</p>
            </div>
            <div className="rf-landing-step-connector">
              <i className="fas fa-arrow-right"></i>
            </div>
            <div className="rf-landing-step">
              <div className="rf-landing-step-number">
                <img src="/images/Rapid Fusion Website Icons/22.svg" alt="Step 2" className="rf-landing-step-icon" />
              </div>
              <h3>Log In</h3>
              <p>Once approved, log in with your credentials to access the full portal.</p>
            </div>
            <div className="rf-landing-step-connector">
              <i className="fas fa-arrow-right"></i>
            </div>
            <div className="rf-landing-step">
              <div className="rf-landing-step-number">
                <img src="/images/Rapid Fusion Website Icons/23.svg" alt="Step 3" className="rf-landing-step-icon" />
              </div>
              <h3>Access Resources</h3>
              <p>Explore tools, training, documentation, and sales resources tailored to your role.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="rf-landing-cta">
        <div className="rf-container">
          <h2>Ready to Get Started?</h2>
          <p>Join the Rapid Fusion network and gain access to everything you need to succeed.</p>
          <div className="rf-landing-cta-actions">
            <Link to="/request-access" className="rf-btn rf-btn-primary rf-btn-lg">
              <i className="fas fa-user-plus"></i>
              Request Access
            </Link>
            <Link to="/check-status" className="rf-landing-cta-link">
              <i className="fas fa-search"></i>
              Already submitted a request? Check your status
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
